import {
  AccountBalanceEnquiry,
  AccountInitData,
  AccountLien,
  IAccountLienQueryRequest,
  AccountStatement,
  AccountTransactionLimit,
  NameValuePair,
  Result,
  TransactionTypes,
} from "../models/interfaces";
import { BaseService } from "./BaseService";

export type AccountDto = {
  id?: number;
  name?: string;
  accountNo?: string;
  accountTypeName?: string;
  accountTypeId?: number;
  accountGroupId?: number;
  accountGroupName?: string;
  customerId?: number;
  customerName?: string;
  marketerId?: number;
  officerId?: number;
  allowDebitBalance?: boolean;
  currencyId?: number;
  enableSMSAlert?: boolean;
  enableEmailAlert?: boolean;
  smsAlertPhoneNo?: string;
  emailAlertAddress?: string;
  businessId?: number;
  locationId?: number;
};

export type GetAccountQuery = {
  query?: string; //accountNo, accountName, accountType etc,
  pageIndex?: number;
  pageSize?: number;
};

export type Account = {
  id?: number;
  name?: string;
  accountTypeId?: number;
  accountGroupId?: number;
  customerId?: number;
  marketerId?: number;
  officerId?: number;
  allowDebitBalance?: boolean;
  currencyId?: number;
  enableSMSAlert?: boolean;
  enableEmailAlert?: boolean;
  smsAlertPhoneNo?: string;
  emailAlertAddress?: string;
};

class AccountService extends BaseService {
  async getAccount(accountId: number): Promise<Result<AccountDto>> {
    return this.get(`accounts/${accountId}`);
  }

  async getAccounts(
    params: GetAccountQuery
  ): Promise<Result<AccountDto[]> | undefined> {
    try {
      return await this.get(`accounts`, params);
    } catch (err) {
      console.error(err);
      const res: Result<AccountDto[]> = {
        data: [
          { id: 1, name: "Hello world", accountTypeName: "sample account" },
        ],
        message: "4 accounts returned",
        status: true,
        code: 2,
      };

      console.log(res);
      return new Promise(() => res);
    }
  }

  async deleteAccount(accountId: number): Promise<Result<boolean>> {
    return await this.delete(`accounts/${accountId}`);
  }
  async closeAccount(accountId: number): Promise<Result<boolean>> {
    return await this.post(`accounts/close-account/${accountId}`);
  }

  async createOrUpdateAccount(
    account: Account,
    id?: number
  ): Promise<Result<AccountDto | boolean>> {
    if (id) {
      return this.put(`accounts/${id}`, account);
    } else {
      return this.post(`accounts`, account);
    }
  }

  async getAccountByAccountNo(
    accountNo: string
  ): Promise<Result<AccountDto | undefined>> {
    const res = (await this.get(`accounts/${accountNo}`)) as Result<
      AccountDto | undefined
    >;
    return res;
  }

  async getTransactionTypes(
    accountId: number
  ): Promise<Result<TransactionTypes> | any> {
    return await this.get<Result<TransactionTypes>>(
      `accounts/${accountId}/transaction-types`
    );
  }

  async setAccountTransactionTypes(
    accountId: number,
    transactionTypes: string[],
    smsTransactionTypes: string[]
  ) {
    return await this.post(`accounts/${accountId}/transaction-types`, {
      transactionTypes,
      smsTransactionTypes,
    });
  }

  async setTransactionTypes(params: {
    accountId: number;
    transactionTypes: string[];
    type: number;
  }): Promise<Result<boolean>> {
    return this.post<Result<boolean>>(
      `accounts/${params.accountId}/transaction-types/${params.type}`,
      params.transactionTypes
    );
  }

  async setLienOnAccount(params: {
    accountId: number;
    amount: number;
    reason: string;
  }): Promise<Result<boolean>> {
    return this.post<Result<boolean>>(
      `accounts/${params.accountId}/lien`,
      params
    );
  }

  async getLienOnAccount(
    params: IAccountLienQueryRequest
  ): Promise<Result<AccountLien[]>> {
    let queryParams: string = "?";
    if (params.isActive !== undefined)
      queryParams += "isActive=" + params.isActive;

    if (params.startDate !== undefined)
      queryParams += "startDate=" + params.startDate;

    if (params.endDate !== undefined)
      queryParams += "endDate=" + params.endDate;

    return this.get<Result<AccountLien[]>>(
      `accounts/${params.accountId}/lien/${queryParams}`
    );
  }

  async removeLienOnAccount(params: {
    accountId: number;
    lienId: number;
  }): Promise<Result<boolean>> {
    return await this.delete<Result<boolean>>(
      `accounts/${params.accountId}/lien/${params.lienId}`
    );
  }

  async getBalanceEnquiry(
    accountId: number
  ): Promise<Result<AccountBalanceEnquiry>> {
    return await this.get<Result<AccountBalanceEnquiry>>(
      `accounts/${accountId}/enquiry`
    );
  }

  async getAccountStatement(params: {
    accountId: number;
    startDate: Date;
    endDate: Date;
  }): Promise<any> {
    return await this.get<Result<AccountStatement>>(
      `accounts/${params.accountId}?from=${params.startDate}&to=${params.endDate}`
    );
  }

  async getInitData(): Promise<AccountInitData | undefined> {
    const resp = await this.get<Result<AccountInitData>>("accounts/init");
    if (resp?.status) {
      return resp?.data;
    }
  }

  async getTransactionLimits(
    id: number
  ): Promise<Result<AccountTransactionLimit[]>> {
    return this.get(`accounts/${id}/transaction-limit`);
  }

  async removeTransactionLimit(
    id: number,
    transactionLimitId: string
  ): Promise<Result<boolean>> {
    return this.delete(
      `accounts/${id}/transaction-limit/${transactionLimitId}`
    );
  }

  async setTransactionLimit(
    accountId: number,
    params: {
      transactionType?: string;
      period?: number;
      amount?: number;
    }
  ): Promise<Result<AccountTransactionLimit>> {
    return this.post(`accounts/${accountId}/transaction-limit`, params);
  }
}

export default new AccountService();

export const cacheKeys = {
  accountType: "accountType",
  accountGroup: "accountGroup",
  transactionTypes: "transactionTypes",
  currency: "currency",
  marketers: "marketers",
  officers: "officers",
};

export class AccountCacheService {
  getAccountTypes(): NameValuePair[] {
    const accountTypeString: string | null = sessionStorage.getItem(
      cacheKeys.accountType
    ) as string;
    return JSON.parse(accountTypeString);
  }

  setAccountTypes(value: NameValuePair[]): void {
    sessionStorage.setItem(cacheKeys.accountType, JSON.stringify(value));
  }

  getAccountGroups(): NameValuePair[] {
    const value = sessionStorage.getItem(cacheKeys.accountGroup) as string;
    return JSON.parse(value);
  }

  setAccountGroups(accountGroups: NameValuePair[]): void {
    sessionStorage.setItem(
      cacheKeys.accountGroup,
      JSON.stringify(accountGroups)
    );
  }

  getTransationTypes(): NameValuePair[] {
    const value = sessionStorage.getItem(cacheKeys.transactionTypes) as string;
    return JSON.parse(value);
  }

  setTransactionTypes(transactionTypes: NameValuePair[]): void {
    sessionStorage.setItem(
      cacheKeys.transactionTypes,
      JSON.stringify(transactionTypes)
    );
  }

  setCurrencies(currencies?: NameValuePair[]) {
    sessionStorage.setItem(cacheKeys.currency, JSON.stringify(currencies));
  }

  getCurrencies(): NameValuePair[] {
    return JSON.parse(sessionStorage.getItem(cacheKeys.currency) as string);
  }

  getAccountMarketers(): NameValuePair[] {
    return JSON.parse(sessionStorage.getItem("marketers") as string);
  }

  setMarketers(marketers: NameValuePair[]) {
    sessionStorage.setItem("marketers", JSON.stringify(marketers));
  }

  getAccountOfficers(): NameValuePair[] {
    return JSON.parse(sessionStorage.getItem("officers") as string);
  }

  setOfficers(marketers: NameValuePair[]) {
    sessionStorage.setItem("officers", JSON.stringify(marketers));
  }
}
